import { useState, useEffect } from 'react';
import { Alert, Form, Input, InputNumber, Image, Row, Col, Upload } from 'antd';
import { SaveOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { NSPButton } from '@components/ui/NSPButton';
import { useUpdateOattNspCoreInfo } from '@hooks/useOattNspCoreInfo';
import { useApi } from '@hooks/useApi';
import type { OattNspCoreInfo } from '@custom-types/oattNspCoreInfo';
import styles from './OattNspCoreInfoPage.module.css';

type OattNspCoreInfoPanelProps = {
    entry: OattNspCoreInfo;
    onSaved: () => void;
    readOnly?: boolean;
};

export function OattNspCoreInfoPanel({ entry, onSaved, readOnly = false }: OattNspCoreInfoPanelProps) {
    const [form] = Form.useForm();
    const { handleUpdate, updating } = useUpdateOattNspCoreInfo();
    const { uploadNspLogo } = useApi();
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [feedback, setFeedback] = useState<{ status: 'success' | 'error'; message: string } | null>(null);

    useEffect(() => {
        form.setFieldsValue({
            company_name: entry.company_name,
            address: entry.address,
            city: entry.city,
            province: entry.province,
            postal_code: entry.postal_code,
            hst_rate: entry.hst_rate,
            approver_email: entry.approver_email,
            contact_name: entry.contact_name,
            contact_phone: entry.contact_phone,
            contact_email: entry.contact_email,
        });
    }, [entry, form]);

    const handleSave = async () => {
        setFeedback(null);
        try {
            const values = await form.validateFields();
            const result = await handleUpdate(entry.oatt_nsp_core_info_id, {
                company_name: values.company_name,
                address: values.address,
                city: values.city,
                province: values.province,
                postal_code: values.postal_code,
                hst_rate: values.hst_rate,
                approver_email: values.approver_email,
                contact_name: values.contact_name,
                contact_phone: values.contact_phone,
                contact_email: values.contact_email,
            });
            if (result.success) {
                setFeedback({ status: 'success', message: 'OATT NSP Core Info updated successfully.' });
                onSaved();
            } else {
                throw new Error(result.error || 'Update failed');
            }
        } catch (err) {
            setFeedback({
                status: 'error',
                message: err instanceof Error ? err.message : 'Failed to update OATT NSP Core Info.',
            });
        }
    };

    const handleLogoUpload = async (file: File) => {
        setUploadingLogo(true);
        try {
            await uploadNspLogo(file, entry.entry_type);
            onSaved();
            setFileList([]);
            setFeedback({ status: 'success', message: 'Logo uploaded successfully.' });
        } catch {
            setFeedback({ status: 'error', message: 'Failed to upload logo.' });
        } finally {
            setUploadingLogo(false);
        }
        return false;
    };

    return (
        <Form form={form} layout="vertical" onFinish={handleSave}>
            {feedback && (
                <Alert
                    className={styles.feedbackAlert}
                    message={feedback.status === 'success' ? 'Success' : 'Error'}
                    description={feedback.message}
                    type={feedback.status}
                    showIcon
                    closable
                    onClose={() => setFeedback(null)}
                />
            )}

            <div className={styles.logoSection}>
                {entry.logo_url && (
                    <Image src={entry.logo_url} alt="NSP Logo" height={80} className={styles.logoPreview} preview />
                )}
                {!readOnly && (
                    <Upload
                        beforeUpload={handleLogoUpload}
                        fileList={fileList}
                        onChange={({ fileList: fl }) => setFileList(fl)}
                        accept="image/*"
                        maxCount={1}
                        showUploadList={false}
                    >
                        <NSPButton icon={<UploadOutlined />} loading={uploadingLogo}>
                            {entry.logo_url ? 'Replace Logo' : 'Upload Logo'}
                        </NSPButton>
                    </Upload>
                )}
            </div>

            <div className={styles.sectionTitle}>Company Information</div>
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <Form.Item label="Company Name" name="company_name" rules={[{ required: true, message: 'Required' }]}>
                        <Input disabled={readOnly} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="HST Rate (%)"
                        name="hst_rate"
                        rules={[{ required: true, message: 'Required' }, { type: 'number', min: 0, max: 100, message: '0–100' }]}
                    >
                        <InputNumber style={{ width: '100%' }} precision={2} placeholder="15.00" disabled={readOnly} />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <Form.Item label="Address" name="address" rules={[{ required: true, message: 'Required' }]}>
                        <Input disabled={readOnly} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item label="City" name="city" rules={[{ required: true, message: 'Required' }]}>
                        <Input disabled={readOnly} />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <Form.Item label="Province" name="province" rules={[{ required: true, message: 'Required' }]}>
                        <Input disabled={readOnly} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="Postal Code"
                        name="postal_code"
                        rules={[
                            { required: true, message: 'Required' },
                            { pattern: /^[A-Z]\d[A-Z] \d[A-Z]\d$/, message: 'e.g. A1A 1A1' },
                        ]}
                    >
                        <Input placeholder="A1A 1A1" disabled={readOnly} />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="Approver Email"
                        name="approver_email"
                        rules={[{ required: true, message: 'Required' }, { type: 'email', message: 'Invalid email' }]}
                    >
                        <Input placeholder="approver@example.com" disabled={readOnly} />
                    </Form.Item>
                </Col>
            </Row>

            <div className={styles.sectionTitle}>Contact Information</div>
            <Row gutter={24}>
                <Col xs={24} md={8}>
                    <Form.Item label="Contact Name" name="contact_name" rules={[{ required: true, message: 'Required' }]}>
                        <Input disabled={readOnly} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item
                        label="Contact Phone"
                        name="contact_phone"
                        rules={[
                            { required: true, message: 'Required' },
                            { pattern: /^(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, message: 'Invalid phone' },
                        ]}
                    >
                        <Input placeholder="(###) ###-####" disabled={readOnly} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item
                        label="Contact Email"
                        name="contact_email"
                        rules={[{ required: true, message: 'Invalid email' }]}
                    >
                        <Input disabled={readOnly} />
                    </Form.Item>
                </Col>
            </Row>

            {!readOnly && (
                <Form.Item>
                    <NSPButton htmlType="submit" icon={<SaveOutlined />} loading={updating}>
                        Save Changes
                    </NSPButton>
                </Form.Item>
            )}
        </Form>
    );
}
